/**
 * Extracted-field review — what a document actually populated, and what a
 * human decided about it.
 *
 * WHY THIS EXISTS. Uploading a document silently filled SED fields via
 * `stampRequirementDocAutoFill`, and the only way to see WHICH fields came
 * from WHICH document was the Coverage Matrix chip-strip two surfaces away
 * from where the user was working. The ledger said "extraction hasn't
 * confirmed the required data yet" without ever showing what it HAD extracted.
 * So the user could neither check the machine's work nor correct it.
 *
 * WHAT A REVIEW IS — AND IS NOT. Accepting an extraction means "I checked this
 * against the document and it matches". It does NOT upgrade the evidence: the
 * document remains the source, and its grade is unchanged. This mirrors the
 * existing LandGate review precedent in `siteEvidence.ts`, where a
 * client-recorded review carries `provenanceAuthority: 'client-recorded-review'`
 * and is explicitly `citationEligible: false`.
 *
 * That is why a review is stored as per-field PROVENANCE METADATA rather than
 * a new `status` value. `status` drives ranking, matrix colour, readiness and
 * report eligibility; adding a member means auditing every consumer, and this
 * codebase has repeatedly shown consumers drifting from the model. Metadata is
 * additive and cannot silently promote anything.
 *
 * The asymmetry is deliberate and is the whole point:
 *   - ACCEPT   records that a human checked it. Evidence strength unchanged.
 *   - CORRECT  replaces the value with the human's, which IS a real upgrade —
 *              it becomes user-authored rather than machine-extracted.
 *   - REJECT   removes a wrong value. This is the load-bearing one: an
 *              unreviewed bad extraction otherwise sits in the SED looking
 *              exactly like a good one.
 */
import type { SiteEvidenceDocument, StructuredEvidenceValue } from '../../types/siteEvidence';
import { fieldsForRequirement, sectionIdFromFieldPath, leafKeyFromFieldPath } from '../../config/requirementToFields';
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';

export type FieldReviewDecision = 'accepted' | 'corrected' | 'rejected';

/** One human decision about one extracted field. */
export interface FieldReview {
  decision: FieldReviewDecision;
  reviewedAt: string;
  reviewedBy: string;
  /** Value present when the decision was taken — kept so a reject is undoable. */
  previousValue?: StructuredEvidenceValue;
}

/** A field a document populated, with any decision already taken on it. */
export interface ExtractedFieldRow {
  /** Dotted SED path, e.g. `parcel.acres`. */
  fieldPath: string;
  sectionId: string;
  fieldKey: string;
  /** Current value in the SED slot, or undefined once rejected. */
  value?: StructuredEvidenceValue;
  /** Prior decision, if this field has been reviewed. */
  review?: FieldReview;
}

/** Shape of the slot fields this module reads. Narrow by design. */
interface ReviewableSlot {
  provenanceDetail?: {
    autoFillFields?: Record<string, StructuredEvidenceValue>;
    fieldReviews?: Record<string, FieldReview>;
    docId?: string;
  };
}

function slotFor(sed: SiteEvidenceDocument | null | undefined, sectionId: string): ReviewableSlot | undefined {
  if (!sed) return undefined;
  return (sed as unknown as Record<string, ReviewableSlot | undefined>)[sectionId];
}

/**
 * Every field a document populated for this requirement, with its current
 * value and any review already recorded.
 *
 * Returns [] when the requirement has no field registry entry or nothing was
 * extracted — callers must render nothing rather than an empty review panel,
 * because "no extracted fields" and "nothing to check" are the same statement.
 */
export function extractedFieldsForRequirement(
  sed: SiteEvidenceDocument | null | undefined,
  requirementId: string,
  requirement?: Pick<import('../../config/documentChecklists').ChecklistItem, 'authoredSchema'>,
): ExtractedFieldRow[] {
  const rows: ExtractedFieldRow[] = [];
  const seen = new Set<string>();
  const push = (fieldPath: string, sectionId: string, fieldKey: string) => {
    if (seen.has(fieldKey)) return; // one row per key, whichever route found it
    const detail = slotFor(sed, sectionId)?.provenanceDetail;
    const value = detail?.autoFillFields?.[fieldKey];
    const review = detail?.fieldReviews?.[fieldKey];
    // Only surface fields the extraction actually touched. A field that was
    // never populated belongs to the "still missing" story, not the review one.
    if (value === undefined && review === undefined) return;
    seen.add(fieldKey);
    rows.push({ fieldPath, sectionId, fieldKey, value, review });
  };

  // D12 — stamps are keyed by RAW SCHEMA keys (`bearingCapacity_kPa`,
  // `bfe_ft`), but the registry walk below derives REGISTRY LEAF keys
  // (`bearingCapacityKPa`), so most extracted fields were invisible to the
  // review panel: the machine stamped them and the human was never shown
  // them. When the caller supplies the checklist item, its authoredSchema
  // keys ARE the stamp keys — walk those against the requirement's primary
  // section first.
  const primarySectionId = REQUIREMENT_TO_SECTION_MAP[requirementId]?.[0];
  if (requirement?.authoredSchema && primarySectionId) {
    for (const field of requirement.authoredSchema) {
      if (field.key === 'notes') continue;
      push(`${primarySectionId}.${field.key}`, primarySectionId, field.key);
    }
  }

  // Registry-path fallback, kept as a union: pre-existing reviews recorded
  // under registry leaf keys must keep rendering, and callers without the
  // checklist item retain the historical behaviour unchanged.
  for (const fieldPath of fieldsForRequirement(requirementId)) {
    push(fieldPath, sectionIdFromFieldPath(fieldPath), leafKeyFromFieldPath(fieldPath));
  }
  return rows;
}

/** Counts driving the review panel's headline. */
export interface ReviewProgress {
  total: number;
  accepted: number;
  corrected: number;
  rejected: number;
  /** Extracted fields with no decision yet — the actual work remaining. */
  unreviewed: number;
}

export function reviewProgress(rows: readonly ExtractedFieldRow[]): ReviewProgress {
  let accepted = 0; let corrected = 0; let rejected = 0;
  for (const r of rows) {
    if (r.review?.decision === 'accepted') accepted++;
    else if (r.review?.decision === 'corrected') corrected++;
    else if (r.review?.decision === 'rejected') rejected++;
  }
  return {
    total: rows.length,
    accepted,
    corrected,
    rejected,
    unreviewed: rows.length - accepted - corrected - rejected,
  };
}

/**
 * Apply one decision to the SED, returning the fields to persist.
 *
 * Pure: takes the current slot state and returns the next one, so the caller
 * owns the write and this stays unit-testable without Firestore.
 *
 * A REJECT deletes the value from `autoFillFields` — leaving a value the human
 * has called wrong would be worse than never extracting it, because everything
 * downstream treats a present value as evidence. `previousValue` is retained on
 * the review so the removal is reversible.
 */
export function applyFieldReview(
  current: {
    autoFillFields?: Record<string, StructuredEvidenceValue>;
    fieldReviews?: Record<string, FieldReview>;
  } | undefined,
  fieldKey: string,
  decision: FieldReviewDecision,
  reviewedBy: string,
  correctedValue?: StructuredEvidenceValue,
): {
  autoFillFields: Record<string, StructuredEvidenceValue>;
  fieldReviews: Record<string, FieldReview>;
} {
  const autoFillFields = { ...(current?.autoFillFields ?? {}) };
  const fieldReviews = { ...(current?.fieldReviews ?? {}) };
  const previousValue = autoFillFields[fieldKey];

  if (decision === 'rejected') {
    delete autoFillFields[fieldKey];
  } else if (decision === 'corrected') {
    if (correctedValue === undefined) {
      // A correction with no value is a reject wearing the wrong label; refuse
      // rather than silently recording an unchanged value as "corrected".
      throw new Error('applyFieldReview: a corrected decision requires a value.');
    }
    autoFillFields[fieldKey] = correctedValue;
  }

  fieldReviews[fieldKey] = {
    decision,
    reviewedAt: new Date().toISOString(),
    reviewedBy,
    ...(previousValue !== undefined ? { previousValue } : {}),
  };
  return { autoFillFields, fieldReviews };
}

/** Undo a review — restores any value the decision removed or replaced. */
export function undoFieldReview(
  current: {
    autoFillFields?: Record<string, StructuredEvidenceValue>;
    fieldReviews?: Record<string, FieldReview>;
  } | undefined,
  fieldKey: string,
): {
  autoFillFields: Record<string, StructuredEvidenceValue>;
  fieldReviews: Record<string, FieldReview>;
} {
  const autoFillFields = { ...(current?.autoFillFields ?? {}) };
  const fieldReviews = { ...(current?.fieldReviews ?? {}) };
  const prior = fieldReviews[fieldKey];
  if (prior?.previousValue !== undefined) {
    autoFillFields[fieldKey] = prior.previousValue;
  } else if (prior && prior.decision !== 'rejected') {
    // Reviewed a field that had no value and was not a rejection — there is
    // nothing to restore, so leave the slot as it is.
  }
  delete fieldReviews[fieldKey];
  return { autoFillFields, fieldReviews };
}
